import _ from 'lodash';
import { getElementPreset, getChildElementPreset, getElementDefinitionProps } from 'crazyEditor/core/elements/utils/elementDefinitionUtils';
import { getMaxZIndex } from 'crazyEditor/core/elements/utils/elementFilterUtils';
import { getPageElement, PAGE_ELEMENT_ID } from 'crazyEditor/core/elements/utils/elementStaticUtils';
import { shouldExpandPageElement } from 'crazyEditor/core/elements/utils/elementPageUtils';
import * as elementActionTypes from '../actions/desktopElementActions';
import update from 'react-addons-update';
import { getElementList } from '../helpers/elementListHelpers';
import { generateElementId } from '../helpers/elementUniqueIdHelpers';
import { initChildElements, getChildrenElementsUpdate, getNestedChildrenUpdateByAction } from '../helpers/elementChildrenHelpers';
import { getYPositionAtMiddleOfView, getUpdatedElementPosition } from '../helpers/elementPositionHelpers';
import { getFixedBorderStyle } from '../helpers/elementBorderHelpers';
import { getMobileId } from 'crazyEditor/core/elements/dataConversation/mobileConversation';
import { SPACE_BETWEEN_MOBILE_ELEMENTS, CONTAINING_MOBILE_ELEMENT_BOUNDRIES, MOBILE_MIN_HEIGHT } from 'crazyEditor/core/constants/editorConstants';
import { getUpdatedMobileElementsAfterDelete } from '../helpers/mobileElementHelpers';
import { LOCATION_CHANGE } from 'react-router-redux';
import { VIEW_MODES } from 'crazyEditor/core/constants/editorConstants';

const LIST_PROPS = ['data.list'];

export default function reducer(state = {}, action = {}) {
  let elementLayout;
  let element;
  let pageElement;
  let newElement;
  let elementId;
  let newElementId;
  let originalElement;
  let elementList;
  let newElements;
  let updatedElement;
  let elementsUpdate;
  let newElementDefinitionProps;

  switch (action.type) {
    case elementActionTypes.ADD_DESKTOP_ELEMENT:
      pageElement = getPageElement(state.desktop.staticElements);
      newElement = getElementPreset(action.elementType);
      newElementDefinitionProps = getElementDefinitionProps(action.elementType);
      const elementPosition = action.elementPosition ? action.elementPosition : {
        x: (pageElement.layout.width - newElement.layout.width) / 2,
        y: getYPositionAtMiddleOfView() - (newElement.layout.height / 2),
      };

      if (newElementDefinitionProps.fullWidth) {
        elementPosition.x = 0;
      }

      newElementId = generateElementId(state.desktop.elements);
      const zIndex = getMaxZIndex(state.desktop.elements) + 1;
      _.merge(newElement,
        action.elementProps,
        {
          id: newElementId,
          type: action.elementType,
          compProps: {
            zIndex
          },
          layout: elementPosition,
        }
      );

      LIST_PROPS.forEach((key) => { // override merged lists
        const value = _.get(action.elementProps, key);
        if (!_.isNil(value)) {
          _.set(newElement, key, value);
        }
      });

      _.forEach(newElement.layout, (value, key) => {
        if (_.isString(value)) {
          newElement.layout[key] = parseInt(value, 10);
        }
      });

      initChildElements(newElement);

      if (shouldExpandPageElement(newElement.layout, pageElement.layout)) {
        return update(state, {
          desktop: {
            staticElements: {
              [PAGE_ELEMENT_ID]: {
                layout: {
                  height: {
                    $set: newElement.layout.y + newElement.layout.height
                  }
                }
              }
            },
            elements: {
              [newElementId]: {
                $set: newElement
              }
            }
          }
        });
      }
      return update(state, {
        desktop: {
          elements: {
            [newElementId]: {
              $set: newElement
            }
          }
        }
      });
    case elementActionTypes.UPDATE_DESKTOP_ELEMENT:
      elementId = _.isArray(action.ids) ? action.ids[0] : action.ids;
      elementList = getElementList(elementId);
      element = state.desktop[elementList][elementId];
      if (!element) {
        elementsUpdate = getNestedChildrenUpdateByAction(elementId, state.desktop[elementList], action);
      } else {
        elementsUpdate = getChildrenElementsUpdate(element, action);
      }
      if (!elementsUpdate) {
        updatedElement = _.merge({}, element, getFixedBorderStyle(element, action.elementProps));
        LIST_PROPS.forEach((key) => { // override merged lists
          const value = _.get(action.elementProps, key);
          if (!_.isNil(value)) {
            _.set(updatedElement, key, value);
          }
        });
        elementsUpdate = {
          [elementId]: {
            $set: updatedElement
          }
        };
      }

      return update(state, {
        desktop: {
          [elementList]: elementsUpdate
        },
        preview: {
          $set: null
        }
      });
    case elementActionTypes.DELETE_ELEMENT:
      const idsToDelete = [];
      if (!_.isArray(action.ids)) {
        action.ids = [action.ids];
      }
      let parentIdToRemove;
      action.ids.forEach((id) => {
        if (!state.desktop.elements[id]) {
          return;
        }
        idsToDelete.push(id);
        parentIdToRemove = state.desktop.elements[id].parentId;
        if (parentIdToRemove) {
          idsToDelete.push(parentIdToRemove);
        }
      });
      _.forEach(state.desktop.elements, (element) => { // delete children
        if (action.ids.indexOf(element.parentId) !== -1 || (parentIdToRemove && element.parentId === parentIdToRemove)) {
          idsToDelete.push(element.id);
        }
      });

      const updatedPositionMobileElements = getUpdatedMobileElementsAfterDelete(state, _.filter(_.map(idsToDelete, (id) => {
        const mobileElement = _.find(state.mobile.elements, ['desktopId', id]);
        if (mobileElement) {
          return mobileElement.id;
        }
        return null;
      }), (mobileId) => mobileId), getPageElement(state.mobile.staticElements));
      return update(state, {
        mobile: {
          elements: {
            $set: _.omitBy(updatedPositionMobileElements.elements, (element) => idsToDelete.indexOf(element.desktopId) !== -1)
          },
          staticElements: updatedPositionMobileElements.staticElements
        },
        desktop: {
          elements: {
            $set: _.omit(state.desktop.elements, idsToDelete)
          }
        }
      });
    case elementActionTypes.DUPLICATE_DESKTOP_ELEMENT:
      const elementsToDuplicate = [];
      newElements = {};
      if (!_.isArray(action.ids)) {
        action.ids = [action.ids];
      }

      action.ids.forEach((elementId) => {
        originalElement = state.desktop.elements[elementId];
        newElement = _.cloneDeep(originalElement);
        elementLayout = {
          ...originalElement.layout,
          x: originalElement.layout.x + 20,
          y: originalElement.layout.y + 20
        };
        newElementId = generateElementId(state.desktop.elements);
        _.merge(newElement, {
          id: newElementId,
          layout: elementLayout,
          compProps: {
            zIndex: getMaxZIndex(state.desktop.elements)
          }
        });

        initChildElements(newElement);

        newElements[newElementId] = {
          $set: newElement
        };
      });
      return update(state, {
        desktop: {
          elements: newElements,
        }
      });
    case elementActionTypes.PASTE_ELEMENTS:
      const desktopUpdate = {};
      _.forEach(action.elements.desktop, element => {
        const newElementId = generateElementId(state.desktop.elements);
        const newElement = {
          ...element,
          compProps: {
            ...element.compProps,
            zIndex: getMaxZIndex(state.desktop.elements) + 1,
          },
          layout: {...element.layout, ...getUpdatedElementPosition('desktop', state, null, element)},
          id: newElementId,
        };
        initChildElements(newElement);
        desktopUpdate[newElementId] = {
          $set: newElement
        };
      });
      return update(state, {
        desktop: {
          elements: desktopUpdate,
        },
      });
    case LOCATION_CHANGE:
      const view = action.payload.query.view;
      return {
        ...state,
        viewMode: view || VIEW_MODES.DESKTOP,
      };
    default:
      return state;
  }
}
